// The DEV/LOCAL fixture switcher's hooks — the app-layer wrapper both shells consume. The mechanism, and why
// its containment is the fixture ADDRESS on both ends rather than an authorization relationship, is
// documented in `app/devFixtureSwitch.ts`.
//
// Here rather than in the shells because `apps/*/src/shells/**` is under the noRawUseQuery rule and this is a
// raw `useQuery` — the dev route is `[ApiExplorerSettings(IgnoreApi)]`, so there is no generated client and no
// tenant-keyed wrapper for it. Same exemption `useBandOwnerPersonId` takes.
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import {
  switcherPossibleInLane,
  fetchFixtureAccounts,
  switchToFixtureAccount,
  returnFromFixtureSwitch,
  type FixtureAccount,
} from "../devFixtureSwitch.js";
import { swapAuthority, useDevImpersonation, type ActingAs } from "../devImpersonation.js";
import { IS_SCREENSHOT_MODE } from "../../config/env.js";

export type { FixtureAccount, ActingAs };

/** Who the app is currently acting as, or null. Drives the switcher button's glow and its label. */
export function useActingAs(): ActingAs | null {
  return useDevImpersonation((s) => s.actingAs);
}

/**
 * Whether to OFFER the switcher — the lane check, then the SERVER's own answer.
 *
 * The list route 404s for a caller it will not serve, so "it returned accounts" IS the permission, and it
 * cannot drift from what a swap would actually do. An earlier version re-implemented the server's `.test`
 * predicate here, which silently hid the button from the one account `DevSwitcher:Initiators` exists for:
 * the rule was written in two places and only one was widened (2026-08-31, dev's founding artist).
 *
 * The extra request is bounded to non-production by the lane check gating `enabled`, and shares one query
 * key with the dialog, so opening it costs nothing further.
 */
export function useCanSwitchFixtureAccounts(): boolean {
  const possible = switcherPossibleInLane() && !IS_SCREENSHOT_MODE;
  const { accounts } = useFixtureAccounts(possible);
  return possible && accounts.length > 0;
}

/**
 * `GET /dev/impersonate` — every fixture account on the box.
 *
 * `retry: false` and a silent error: the route answers 404 both in a production lane and for a non-fixture
 * caller, and both are VERDICTS rather than blips. The dialog renders its empty state rather than an error.
 */
export function useFixtureAccounts(enabled: boolean) {
  const { tokenProvider } = useApp();
  const query = useQuery<FixtureAccount[], Error>({
    queryKey: ["dev", "fixture-switch"],
    // The bearer we STARTED as, not the worn one — the same rule the band swap follows, so a chain of hops
    // is always authorized by the account that began it.
    queryFn: () => fetchFixtureAccounts(swapAuthority(tokenProvider)),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  return { accounts: query.data ?? [], isLoading: query.isLoading, failed: query.isError };
}

/** The switch action, bound to the app's TokenProvider + QueryClient. */
export function useFixtureSwitch(navigate: (to: string) => void | Promise<void>) {
  const { tokenProvider } = useApp();
  const queryClient = useQueryClient();

  return useCallback(
    (personId: string) => switchToFixtureAccount({ tokenProvider, queryClient, navigate }, personId),
    [tokenProvider, queryClient, navigate],
  );
}

/** The return action — the switcher dialog's "Return to my account". */
export function useFixtureReturn(navigate: (to: string) => void | Promise<void>) {
  const { tokenProvider } = useApp();
  const queryClient = useQueryClient();

  return useCallback(
    () => returnFromFixtureSwitch({ tokenProvider, queryClient, navigate }),
    [tokenProvider, queryClient, navigate],
  );
}
